import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../constants/orderFormConstants';

/**
 * Custom hook for fetching wallet balance
 * @returns {Object} - wallet balance and loading state
 */
export const useWalletBalance = () => {
  const [walletBalance, setWalletBalance] = useState(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  useEffect(() => {
    const fetchWalletBalance = async () => {
      try {
        setIsLoadingBalance(true);
        const userId = localStorage.getItem("id");
        const response = await fetch(
          `${API_ENDPOINTS.FAST_URL}/payment.php?userId=${userId}`,
          { method: "GET" }
        );

        const data = await response.json();
        if (typeof data !== "object") {
          setWalletBalance(data);
        } else {
          console.error("Error fetching wallet balance:", data.error);
        }
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
      } finally {
        setIsLoadingBalance(false);
      }
    };

    fetchWalletBalance();
  }, []);

  return { walletBalance, isLoadingBalance };
};

/**
 * Custom hook for fetching categories
 * @returns {Object} - categories and loading state
 */
export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setIsLoadingCategories(true);
      const response = await axios.get(
        `${API_ENDPOINTS.FAST_URL}/categories.php`
      );
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  return { categories, isLoadingCategories, refetchCategories: fetchCategories };
};

/**
 * Custom hook for fetching services based on category name
 * @param {string} categoryName - Selected category name
 * @returns {Object} - services and loading state
 */
export const useServices = (categoryName) => {
  const [serviceList, setServiceList] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      setIsLoadingServices(true);
      const serviceUrl = categoryName
        ? `${API_ENDPOINTS.FAST_URL}/service.php?cat=${categoryName}`
        : `${API_ENDPOINTS.FAST_URL}/service.php`;

      const response = await axios.get(serviceUrl, {
        params: { per_page: 100 }
      });

      const activeServices = response.data.filter(
        (service) => service.status === "active"
      );

      // Only update state if the new list is different to prevent infinite re-renders
      if (JSON.stringify(activeServices) !== JSON.stringify(serviceList)) {
        setServiceList(activeServices);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setIsLoadingServices(false);
    }
  }, [categoryName, serviceList]); // Add serviceList to dependencies for comparison

  useEffect(() => {
    fetchServices();
  }, [categoryName]);

  return { serviceList, isLoadingServices, refetchServices: fetchServices };
};

/**
 * Custom hook for fetching YouTube video data
 * @param {string} videoId - YouTube video ID
 * @returns {Object} - video data and loading state
 */
export const useVideoData = (videoId) => {
  const [videoData, setVideoData] = useState(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);

  const fetchVideoData = useCallback(async (id) => {
    if (!id) {
      setVideoData(null); // Clear video data if ID is null
      return;
    }

    try {
      setIsLoadingVideo(true);
      const apiUrl = `https://www.googleapis.com/youtube/v3/videos?id=${id}&key=${API_ENDPOINTS.YOUTUBE_API_KEY}&part=snippet,contentDetails`;
      const response = await axios.get(apiUrl);
      setVideoData(response.data.items[0] || null);
    } catch (error) {
      console.error("Failed to fetch video info:", error);
      setVideoData(null);
    } finally {
      setIsLoadingVideo(false);
    }
  }, []); // No dependencies, as videoId is passed as an argument

  useEffect(() => {
    fetchVideoData(videoId);
  }, [videoId]); // fetchVideoData is stable due to useCallback

  return { videoData, isLoadingVideo, refetchVideoData: fetchVideoData };
};
